import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Grid2 as Grid,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Tooltip, MenuItem, ImageList, ImageListItem, ImageListItemBar,
} from '@mui/material';
import {
  CloudUpload, Delete, Edit, ContentCopy, Refresh, Image as ImageIcon,
  Folder, CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSMedia } from '../../services';

const PASTAS = ['geral', 'hero', 'equipa', 'features', 'blog', 'eventos', 'logos'];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CMSMediaPage: React.FC = () => {
  const { t } = useTranslation();
  const [media, setMedia] = useState<CMSMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filterPasta, setFilterPasta] = useState<string>('');
  const [selectedMedia, setSelectedMedia] = useState<CMSMedia | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<Partial<CMSMedia>>({});
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadPasta, setUploadPasta] = useState('geral');
  const [uploadAltText, setUploadAltText] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (filterPasta) params.pasta = filterPasta;
      const response = await cmsService.getMedia(params);
      setMedia(response.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingMedia'));
    } finally {
      setLoading(false);
    }
  }, [filterPasta]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);
    let uploadCount = 0;

    for (const file of Array.from(files)) {
      try {
        await cmsService.uploadMedia(file, uploadPasta, uploadAltText || file.name);
        uploadCount++;
      } catch (err: any) {
        setError(t('cms.errorUploading', { name: file.name, error: err.message }));
      }
    }

    setUploading(false);
    setUploadDialog(false);
    setUploadAltText('');

    if (uploadCount > 0) {
      setSuccess(t('cms.filesUploaded', { count: uploadCount }));
      fetchMedia();
    }
  };

  const handleDelete = async (item: CMSMedia) => {
    if (!window.confirm(t('cms.confirmDeleteMedia', { name: item.nome }))) return;

    try {
      await cmsService.deleteMedia(item.id);
      setSuccess(t('cms.fileDeleted'));
      fetchMedia();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingFile'));
    }
  };

  const handleEdit = (item: CMSMedia) => {
    setEditData({ nome: item.nome, alt_text: item.alt_text, pasta: item.pasta });
    setSelectedMedia(item);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedMedia) return;
    try {
      await cmsService.updateMedia(selectedMedia.id, editData as any);
      setSuccess(t('cms.fileUpdated'));
      setEditDialog(false);
      fetchMedia();
    } catch (err: any) {
      setError(err.message || t('cms.errorUpdatingFile'));
    }
  };

  const handleCopyUrl = (item: CMSMedia) => {
    navigator.clipboard.writeText(item.url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{t('cms.mediaLibrary')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.mediaSubtitle', { count: media.length })}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchMedia} disabled={loading}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setUploadDialog(true)}>
            {t('cms.uploadFile')}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filters */}
      <Box mb={3} display="flex" gap={1} flexWrap="wrap">
        <Chip label={t('common.all')} variant={filterPasta === '' ? 'filled' : 'outlined'} color={filterPasta === '' ? 'primary' : 'default'} onClick={() => setFilterPasta('')} />
        {PASTAS.map(p => (
          <Chip key={p} label={p.charAt(0).toUpperCase() + p.slice(1)} variant={filterPasta === p ? 'filled' : 'outlined'} color={filterPasta === p ? 'primary' : 'default'} onClick={() => setFilterPasta(p)} icon={<Folder fontSize="small" />} />
        ))}
      </Box>

      {/* Media Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      ) : media.length === 0 ? (
        <Card><CardContent sx={{ textAlign: 'center', py: 10 }}>
          <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">{t('cms.noFileFound')}</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>{t('cms.startUploadingHint')}</Typography>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setUploadDialog(true)}>{t('cms.uploadFirstFile')}</Button>
        </CardContent></Card>
      ) : (
        <ImageList cols={4} gap={16} sx={{ mb: 4 }}>
          {media.map((item) => (
            <ImageListItem key={item.id} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&:hover .actions': { opacity: 1 } }}>
              {item.tipo.startsWith('image/') ? (
                <img src={item.url} alt={item.alt_text || item.nome} loading="lazy" style={{ height: 200, objectFit: 'cover' }} />
              ) : (
                <Box height={200} display="flex" alignItems="center" justifyContent="center" bgcolor="action.hover">
                  <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                </Box>
              )}
              <ImageListItemBar
                title={item.nome}
                subtitle={
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip size="small" label={item.pasta} sx={{ height: 20, fontSize: 10 }} />
                    {item.tamanho && <Typography variant="caption">{formatFileSize(item.tamanho)}</Typography>}
                  </Box>
                }
                actionIcon={
                  <Box className="actions" sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.5, pr: 1 }}>
                    <Tooltip title={copiedId === item.id ? t('cms.copiedUrl') : t('cms.copyUrl')}>
                      <IconButton size="small" sx={{ color: 'white' }} onClick={() => handleCopyUrl(item)}>
                        {copiedId === item.id ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" sx={{ color: 'white' }} onClick={() => handleEdit(item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('cms.deleteFile')}>
                      <IconButton size="small" sx={{ color: '#ef4444' }} onClick={() => handleDelete(item)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cms.uploadFiles')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField select fullWidth label={t('cms.folder')} value={uploadPasta} onChange={(e) => setUploadPasta(e.target.value)}>
                {PASTAS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label={t('cms.altText')} value={uploadAltText} onChange={(e) => setUploadAltText(e.target.value)} placeholder={t('cms.altTextPlaceholder')} />
            </Grid>
            <Grid size={12}>
              <Box
                sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' } }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
              >
                <CloudUpload sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" fontWeight="bold" color="text.secondary">
                  {t('cms.dragDropHint')}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {t('cms.fileFormats')}
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => handleUpload(e.target.files)}
                />
              </Box>
            </Grid>
          </Grid>
          {uploading && (
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">{t('cms.uploading')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cms.editFile')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {selectedMedia?.tipo.startsWith('image/') && (
              <Grid size={12}>
                <Box textAlign="center">
                  <img src={selectedMedia.url} alt={selectedMedia.nome} style={{ maxHeight: 200, borderRadius: 8 }} />
                </Box>
              </Grid>
            )}
            <Grid size={12}>
              <TextField fullWidth label={t('common.name')} value={editData.nome || ''} onChange={(e) => setEditData(prev => ({ ...prev, nome: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label={t('cms.altText')} value={editData.alt_text || ''} onChange={(e) => setEditData(prev => ({ ...prev, alt_text: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField select fullWidth label={t('cms.folder')} value={editData.pasta || 'geral'} onChange={(e) => setEditData(prev => ({ ...prev, pasta: e.target.value }))}>
                {PASTAS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            {selectedMedia && (
              <Grid size={12}>
                <TextField fullWidth label="URL" value={selectedMedia.url} InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 12 } }} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveEdit}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSMediaPage;
